import type React from 'react';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { InputActionMeta, MenuPlacement, MultiValue } from 'react-select';
import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useTheme } from 'styled-components';
import type { Text } from 'styles/config/text';

import { CloseCircleIcon } from 'components/atoms/Icons/CloseCircleIcon';
import type { InputWrapperProps } from 'components/atoms/InputWrapper';
import InputWrapper from 'components/atoms/InputWrapper';
import type { OptionType } from '../Select';
import * as Styled from './CreateSelect.styled';

export type CreateSelectProps = {
  options: OptionType[];
  defaultValue: OptionType[];
  placeholder: string;
  handleCreateOptionFail: () => void;
  onBlur?: (value: string[]) => void;
  onChange?: (value: string[]) => void;
  onCreateOption?: (value: string) => void;
  formatCreateLabel?: (userInput: string) => string;
  allowWhiteSpaceOnCreate?: boolean;
  charLimit?: number;
  charLimitWarningMessage?: string;
  noOptionsMessage?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  disableCreateOption?: boolean;
  hideOptionMenu?: boolean;
  dataTestId?: string;
  handleUnenteredChangesState?: (hasUnenteredChanges: boolean) => void;
  fontWeight?: keyof Text['weight'];
  menuPlacement?: MenuPlacement;
  hideSelectedOptions?: boolean;
  isClearable?: boolean;
} & InputWrapperProps;

const CreateSelect: React.FC<CreateSelectProps> = ({
  options,
  placeholder,
  defaultValue,
  handleCreateOptionFail,
  onBlur,
  onChange,
  onCreateOption = () => {},
  formatCreateLabel,
  allowWhiteSpaceOnCreate = true,
  charLimit = 0,
  charLimitWarningMessage,
  noOptionsMessage = 'No options',
  isDisabled = false,
  isLoading = false,
  disableCreateOption = false,
  hideOptionMenu = false,
  handleUnenteredChangesState,
  fontWeight = 'semibold',
  menuPlacement = 'auto',
  hideSelectedOptions = false,
  isClearable = false,
  ...props
}) => {
  const [selected, setSelected] =
    useState<MultiValue<OptionType>>(defaultValue);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isOverCharLimit, setIsOverCharLimit] = useState(false);
  const [input, setInput] = useState('');

  const {
    activeColorScheme: { colors },
    text: { weight },
  } = useTheme();

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };
  const handleMenuClosed = () => {
    setMenuOpen(false);
    setIsOverCharLimit(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // react-select issue: workaround to avoid refreshing the page when pressing enter
    if (!isMenuOpen && e.key === 'Enter') {
      handleOnBlur();
      e.preventDefault();
    }

    e.stopPropagation();
  };

  const handleOnChange = useCallback(
    (selected: MultiValue<OptionType>) => {
      let updatedSelected = [...selected];
      const lastItem = selected.at(-1);

      // Handle pasted value
      if (lastItem?.value.includes(',')) {
        const splitText = lastItem.value.split(',').map(item => item.trim());

        if (splitText.length) {
          const updatedItems = splitText.map(value => ({
            label: value,
            value,
          }));

          updatedSelected = [...selected.slice(0, -1), ...updatedItems];
        }
      }

      setSelected(updatedSelected);
      if (onChange) {
        onChange?.(updatedSelected.map(opt => opt.value));
      }
    },
    [onChange],
  );

  const handleOnBlur = useCallback(() => {
    if (!onBlur) {
      return;
    }
    const values = selected.map(opt => opt.value);
    onBlur(values);
  }, [onBlur, selected]);

  const handleOnCreateOption = useCallback(
    (value: string) => {
      if (onCreateOption && !isOverCharLimit) {
        setInput('');
        try {
          const cleanedValue = allowWhiteSpaceOnCreate
            ? value
            : value.replace(/ /g, '_');

          onCreateOption(cleanedValue);
          handleOnChange([
            ...selected,
            { label: cleanedValue, value: cleanedValue },
          ]);
        } catch (_e) {
          handleCreateOptionFail();
        }
      }
    },
    [
      allowWhiteSpaceOnCreate,
      handleCreateOptionFail,
      handleOnChange,
      isOverCharLimit,
      onCreateOption,
      selected,
    ],
  );

  const handleCreateLabel = useCallback(
    (inputValue: string) => {
      if (isOverCharLimit && charLimitWarningMessage) {
        return charLimitWarningMessage;
      }

      return formatCreateLabel
        ? formatCreateLabel(inputValue)
        : `Create "${inputValue}"`;
    },
    [charLimitWarningMessage, formatCreateLabel, isOverCharLimit],
  );

  const handleInputChange = (value: string, action: InputActionMeta) => {
    if (action.action === 'input-change') {
      setInput(value);
    }
  };

  useEffect(() => {
    if (handleUnenteredChangesState) {
      handleUnenteredChangesState(!!input);
    }
  }, [input, handleUnenteredChangesState]);

  const customStyles = useMemo(
    () => ({
      control: (base: any) => ({
        ...base,
        borderRadius: '8px',
        fontFamily: 'Proxima Nova',
        fontStyle: 'normal',
        fontWeight: weight[fontWeight],
        fontSize: '14px',
        backgroundColor: colors.background,
        color: colors.textPrimary,

        borderColor: !!props?.errors?.length
          ? colors.borderDanger
          : colors.borderStrong,
        padding: '0.5rem',
        boxShadow: 'none',
        '&:focus-within': {
          borderColor:
            isOverCharLimit || !!props?.errors?.length
              ? colors.borderDanger
              : colors.borderPrimary,
        },
        '&:hover': {
          borderColor: !!props?.errors?.length
            ? colors.borderDanger
            : colors.borderStrong,
          cursor: 'text',
        },
      }),
      option: (base: any, state: any) => ({
        ...base,
        fontFamily: 'Proxima Nova',
        fontStyle: 'normal',
        fontWeight: weight[fontWeight],
        fontSize: '14px',

        backgroundColor: isOverCharLimit
          ? base.backgroundColor
          : state.isFocused
            ? colors.backgroundHover
            : colors.background,
        color: colors.textPrimary,
      }),
      multiValue: (base: any) => ({
        ...base,
        borderRadius: '8px',
        border: `1px solid ${colors.borderStrong}`,
        padding: '0px',
        overflow: 'hidden',
      }),
      multiValueLabel: (base: any) => ({
        ...base,
        backgroundColor: colors.backgroundPage,
        borderRadius: '0px',
        border: 'none',
        padding: '0px',
        paddingTop: '2px',
        fontWeight: weight.semibold,
      }),
      multiValueRemove: (base: any) => ({
        ...base,
        backgroundColor: colors.backgroundPage,
        borderRadius: '0px',
        padding: '0px',
        '&:hover': {
          backgroundColor: colors.backgroundPage,
          cursor: 'pointer',
        },
      }),
      noOptionsMessage: (base: any) => ({
        ...base,
        textAlign: 'left',
        fontFamily: 'Proxima Nova',
        fontStyle: 'normal',
        fontWeight: weight[fontWeight],
        fontSize: '14px',
      }),
      menuList: (base: any) => ({
        ...base,
        backgroundColor: colors.background,
        color: isOverCharLimit ? colors.textHelper : base.color,
      }),
      menu: (base: any) => ({
        ...base,
        zIndex: 3,
      }),
    }),
    [
      colors.background,
      colors.backgroundHover,
      colors.borderStrong,
      colors.borderPrimary,
      colors.backgroundPage,
      colors.textPrimary,
      colors.textHelper,
      colors.borderDanger,
      isOverCharLimit,
      props?.errors,
      fontWeight,
      weight,
    ],
  );

  const MultiValueRemove = (props: any) => (
    <div data-test-id={`remove-${props.data.label}`}>
      <components.MultiValueRemove {...props}>
        <CloseCircleIcon />
      </components.MultiValueRemove>
    </div>
  );

  const Menu = (props: any) => {
    const charLength = props.selectProps.inputValue.length;

    if (charLimit) {
      if (charLength > charLimit) {
        setIsOverCharLimit(true);
      } else {
        setIsOverCharLimit(false);
      }
    }

    if (hideOptionMenu) {
      return null;
    }

    return (
      <>
        <components.Menu {...props} />
        {charLimit && charLength > charLimit ? (
          <Styled.CharLimitError>
            {charLength}/{charLimit}
          </Styled.CharLimitError>
        ) : null}
      </>
    );
  };

  return (
    <InputWrapper {...props}>
      <CreatableSelect
        className="create-select-dropdown"
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          ClearIndicator: () => null,
          MultiValueRemove,
          Menu,
        }}
        isClearable={isClearable}
        defaultValue={defaultValue}
        formatCreateLabel={(inputValue: string) =>
          handleCreateLabel(inputValue)
        }
        inputValue={handleUnenteredChangesState ? input : undefined}
        isDisabled={isLoading || isDisabled}
        isValidNewOption={disableCreateOption ? () => false : undefined}
        menuPlacement={menuPlacement}
        noOptionsMessage={({ inputValue }: { inputValue: string }) =>
          !inputValue && noOptionsMessage
        }
        options={options}
        placeholder={isLoading ? 'Processing' : placeholder}
        styles={customStyles}
        value={isLoading ? [] : hideSelectedOptions ? [] : selected}
        isMulti={true}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        onCreateOption={handleOnCreateOption}
        onInputChange={
          handleUnenteredChangesState ? handleInputChange : undefined
        }
        onKeyDown={handleKeyDown}
        onMenuClose={handleMenuClosed}
        onMenuOpen={handleMenuOpen}
        {...props}
      />
    </InputWrapper>
  );
};

export default CreateSelect;
